import React from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { JSONContent } from '@tiptap/core'

/**
 * Transform Jira ADF content to be compatible with Tiptap
 * Maps Jira's mark types to Tiptap's expected types
 */
const transformADFContent = (content: any): JSONContent => {
  if (!content || typeof content !== 'object') {
    return content
  }

  // Handle arrays
  if (Array.isArray(content)) {
    return content.map(transformADFContent)
  }

  // Transform the current node
  const transformed = { ...content }

  // Transform marks: map Jira ADF marks to Tiptap marks
  if (transformed.marks && Array.isArray(transformed.marks)) {
    transformed.marks = transformed.marks.map((mark: any) => {
      const newMark = { ...mark }
      // Map Jira mark types to Tiptap mark types
      switch (mark.type) {
        case 'strong':
          newMark.type = 'bold'
          break
        case 'em':
          newMark.type = 'italic'
          break
        case 'strike':
          newMark.type = 'strike'
          break
        case 'code':
          newMark.type = 'code'
          break
        // Add more mappings as needed
        default:
          // Keep unknown marks as-is, but they might be filtered out by Tiptap
          break
      }
      return newMark
    })
  }

  // Recursively transform content
  if (transformed.content && Array.isArray(transformed.content)) {
    transformed.content = transformed.content.map(transformADFContent)
  }

  return transformed
}

interface JiraContentRendererProps {
  content: any // ADF JSON content from Jira
  className?: string
}

/**
 * Component to render Jira's Atlassian Document Format (ADF) content
 * Uses Tiptap to convert JSON content to readable HTML
 */
export const JiraContentRenderer: React.FC<JiraContentRendererProps> = ({
  content,
  className = ''
}) => {
  // Transform the ADF content to be compatible with Tiptap
  const transformedContent = React.useMemo(() => {
    try {
      return transformADFContent(content)
    } catch (error) {
      console.warn('Failed to transform ADF content:', error)
      return null
    }
  }, [content])

  // Create a read-only editor instance with comprehensive extensions
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        history: false, // Disable history for read-only
        // Enable all formatting options that Jira might use
        bold: {},
        italic: {},
        strike: {},
        code: {},
        codeBlock: {},
        paragraph: {},
        heading: {},
        bulletList: {},
        orderedList: {},
        listItem: {},
        blockquote: {},
        horizontalRule: {},
        hardBreak: {},
      })
    ],
    content: transformedContent as JSONContent,
    editable: false, // Make it read-only
    editorProps: {
      attributes: {
        class: `prose prose-sm max-w-none focus:outline-none ${className}`,
      },
    },
  })

  // Handle cases where content might not be valid JSON
  if (!content || typeof content !== 'object') {
    return null
  }

  // Handle transformation failures
  if (!transformedContent) {
    const plainText = extractTextFromADF(content)
    return plainText ? (
      <div className={`text-sm text-gray-700 ${className}`}>
        {plainText}
      </div>
    ) : (
      <div className={`text-sm text-gray-600 italic ${className}`}>
        Rich text content available
      </div>
    )
  }

  // Handle Tiptap errors gracefully
  if (!editor) {
    const plainText = extractTextFromADF(content)
    return plainText ? (
      <div className={`text-sm text-gray-700 ${className}`}>
        {plainText}
      </div>
    ) : (
      <div className={`text-sm text-gray-600 italic ${className}`}>
        Unable to render rich text content
      </div>
    )
  }

  return (
    <div className={`jira-content-renderer ${className}`}>
      <EditorContent editor={editor} />
    </div>
  )
}

/**
 * Utility function to extract plain text from ADF content
 * Useful for previews or when rich formatting isn't needed
 */
export const extractTextFromADF = (content: any): string => {
  if (!content || typeof content !== 'object') {
    return ''
  }

  const extractText = (node: any): string => {
    if (node.text) {
      return node.text
    }

    if (node.content && Array.isArray(node.content)) {
      return node.content.map(extractText).join(' ')
    }

    return ''
  }

  if (content.content && Array.isArray(content.content)) {
    return content.content.map(extractText).join('\n').trim()
  }

  return ''
}

export default JiraContentRenderer